import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, IconButton, Stack, useMediaQuery } from "@mui/material";
import { DataGrid, GridAddIcon } from "@mui/x-data-grid";
import dayjs from "dayjs";
import VisibilityIcon from "../../assets/SVGs/Login/VisibilityIcon";
import { MembershipPaymentsAPI } from "../../api/membership-payments";
import { formatCurrency } from "../helper";
import AddMembershipPaymentModal from "../Modal/AddMembershipPaymentModal";
import EditMembershipPaymentModal from "../Modal/EditMembershipPaymentModal";

const MembershipPaymentsTab = ({ id }) => {
  const navigate = useNavigate();
  const isMobileTable = useMediaQuery("(max-width:850px)");
  const [data, setData] = useState({ items: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editPayment, setEditPayment] = useState(null);

  const fetch = async () => {
    setIsLoading(true);
    try {
      setData(await MembershipPaymentsAPI.getPayments(id));
    } catch (e) {
      if (e.response?.status === 401) {
        navigate("/login");
      }
      console.error("Error fetching membership payments:", e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading && id) fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, id]);

  const columns = [
    {
      field: "payment_date",
      headerName: "Payment Date",
      flex: 1,
      type: "date",
      minWidth: isMobileTable ? 100 : undefined,
      valueGetter: (params) => dayjs(params).toDate(),
      renderCell: (params) =>
        params.value ? dayjs(params.value).format("MM/DD/YYYY") : "",
    },
    {
      field: "amount",
      headerName: "Amount",
      flex: 1,
      minWidth: isMobileTable ? 100 : undefined,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: "note",
      headerName: "Note",
      flex: 2,
      minWidth: isMobileTable ? 300 : undefined,
      renderCell: (params) => params.value || "—",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          aria-label="view"
          size="small"
          onClick={() => setEditPayment(params.row)}
        >
          <VisibilityIcon sx={{ color: "#64748B" }} />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
          gap: "16px",
        }}
      >
        <div className="sub-header">Membership Payments</div>
        <Button
          variant="contained"
          startIcon={<GridAddIcon />}
          className="add-new-button"
          onClick={() => setOpenAddModal(true)}
          sx={{ width: "auto !important", flexShrink: 0, px: "20px" }}
        >
          Add Payment
        </Button>
      </Box>
      <Stack
        sx={{
          width: "100%",
          overflowX: "auto",
          "& .MuiDataGrid-root": {
            borderRadius: "8px",
          },
        }}
        className="customDataGridFigma"
      >
        <DataGrid
          rows={data?.items || []}
          columns={columns}
          loading={isLoading}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: {
              sortModel: [{ field: "payment_date", sort: "desc" }],
            },
          }}
          checkboxSelection={false}
          rowSelection={false}
          rowHeight={48}
          columnHeaderHeight={40}
          getRowClassName={(params) => {
            const customClassName =
              params.indexRelativeToCurrentPage % 2 !== 0 ? "bg-gray" : "";
            return ` ${customClassName}`.trim();
          }}
          slotProps={{
            loadingOverlay: {
              variant: "linear-progress",
              noRowsVariant: "linear-progress",
            },
          }}
          localeText={{
            noRowsLabel: "No payments recorded yet.",
          }}
        />
      </Stack>

      {openAddModal && (
        <AddMembershipPaymentModal
          open={openAddModal}
          handleClose={() => setOpenAddModal(false)}
          setIsLoading={setIsLoading}
          memberId={id}
        />
      )}

      {editPayment && (
        <EditMembershipPaymentModal
          open={!!editPayment}
          handleClose={() => setEditPayment(null)}
          setIsLoading={setIsLoading}
          payment={editPayment}
        />
      )}
    </>
  );
};

export default MembershipPaymentsTab;
